/**
 * Local Cache Service
 * ====================
 * Manages chunked data loading with local caching for:
 * - Communities (by category)
 * - Free Agents (by category/skill)
 * - Groups (my groups by type)
 * 
 * Features:
 * - Chunked/paginated loading
 * - Category-based organization
 * - TTL-based cache invalidation
 * - IndexedDB persistence for offline support
 */

const CACHE_VERSION = 1;
const DEFAULT_CHUNK_SIZE = 12;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// In-memory cache store
const cacheStore = {
  communities: {
    byCategory: {},      // { categoryId: { items: [], offset: 0, hasMore: true, lastFetch: null } }
    categories: null,
    lastCategoriesFetch: null,
  },
  freeAgents: {
    byCategory: {},      // { categoryId: { items: [], offset: 0, hasMore: true, lastFetch: null } }
    categories: null,
    lastCategoriesFetch: null,
  },
  myGroups: {
    byType: {},          // { typeId: { items: [], offset: 0, hasMore: true, lastFetch: null } }
    types: null,
    lastTypesFetch: null,
  },
  users: {},             // { odId: { data: {}, lastFetch: null } }
};

// Cache metadata
const cacheMeta = {
  initialized: false,
  userId: null,
};

/**
 * Initialize cache for a user
 */
export const initializeCache = async (userId) => {
  if (cacheMeta.initialized && cacheMeta.userId === userId) {
    return;
  }
  
  // Clear cache if different user
  if (cacheMeta.userId !== userId) {
    clearCache();
  }
  
  cacheMeta.userId = userId;
  cacheMeta.initialized = true;
  
  // Try to restore from IndexedDB
  try {
    await restoreFromIndexedDB(userId);
  } catch (e) {
    console.warn('Could not restore cache from IndexedDB:', e);
  }
};

/**
 * Clear all cached data
 */
export const clearCache = () => {
  cacheStore.communities = { byCategory: {}, categories: null, lastCategoriesFetch: null };
  cacheStore.freeAgents = { byCategory: {}, categories: null, lastCategoriesFetch: null };
  cacheStore.myGroups = { byType: {}, types: null, lastTypesFetch: null };
  cacheStore.users = {};
};

/**
 * Check if cache entry is still valid
 */
const isCacheValid = (lastFetch, ttl = CACHE_TTL) => {
  if (!lastFetch) return false;
  return Date.now() - lastFetch < ttl;
};

// =====================================================
// COMMUNITIES CACHE
// =====================================================

/**
 * Get community categories (with caching)
 */
export const getCommunityCategories = async (fetchFn) => {
  if (cacheStore.communities.categories && isCacheValid(cacheStore.communities.lastCategoriesFetch)) {
    return cacheStore.communities.categories;
  }
  
  const categories = await fetchFn();
  cacheStore.communities.categories = categories;
  cacheStore.communities.lastCategoriesFetch = Date.now();
  
  return categories;
};

/**
 * Get communities for a category (chunked loading)
 */
export const getCommunitiesByCategory = async (categoryId, fetchFn, options = {}) => {
  const { forceRefresh = false, chunkSize = DEFAULT_CHUNK_SIZE } = options;
  const cacheKey = categoryId || 'all';
  
  // Initialize category cache if needed
  if (!cacheStore.communities.byCategory[cacheKey]) {
    cacheStore.communities.byCategory[cacheKey] = {
      items: [],
      offset: 0,
      hasMore: true,
      lastFetch: null,
    };
  }
  
  const categoryCache = cacheStore.communities.byCategory[cacheKey];
  
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && categoryCache.items.length > 0 && isCacheValid(categoryCache.lastFetch)) {
    return {
      items: categoryCache.items,
      hasMore: categoryCache.hasMore,
      fromCache: true,
    };
  }
  
  // Fetch fresh data
  if (forceRefresh) {
    categoryCache.items = [];
    categoryCache.offset = 0;
    categoryCache.hasMore = true;
  }
  
  const result = await fetchFn({
    categoryId,
    limit: chunkSize,
    offset: categoryCache.offset,
  });
  
  const newItems = result.communities || result.items || result || [];
  
  categoryCache.items = [...categoryCache.items, ...newItems];
  categoryCache.offset += newItems.length;
  categoryCache.hasMore = newItems.length >= chunkSize;
  categoryCache.lastFetch = Date.now();
  
  return {
    items: categoryCache.items,
    hasMore: categoryCache.hasMore,
    fromCache: false,
  };
};

/**
 * Load more communities for a category
 */
export const loadMoreCommunities = async (categoryId, fetchFn, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const cacheKey = categoryId || 'all';
  const categoryCache = cacheStore.communities.byCategory[cacheKey];
  
  if (!categoryCache || !categoryCache.hasMore) {
    return { items: categoryCache?.items || [], hasMore: false, loadedMore: false };
  }
  
  const result = await fetchFn({
    categoryId,
    limit: chunkSize,
    offset: categoryCache.offset,
  });
  
  const newItems = result.communities || result.items || result || [];
  
  categoryCache.items = [...categoryCache.items, ...newItems];
  categoryCache.offset += newItems.length;
  categoryCache.hasMore = newItems.length >= chunkSize;
  categoryCache.lastFetch = Date.now();
  
  return {
    items: categoryCache.items,
    hasMore: categoryCache.hasMore,
    loadedMore: newItems.length > 0,
    newItems,
  };
};

// =====================================================
// FREE AGENTS CACHE
// =====================================================

/**
 * Get free agent categories/skills (with caching)
 */
export const getFreeAgentCategories = async (fetchFn) => {
  if (cacheStore.freeAgents.categories && isCacheValid(cacheStore.freeAgents.lastCategoriesFetch)) {
    return cacheStore.freeAgents.categories;
  }
  
  const categories = await fetchFn();
  cacheStore.freeAgents.categories = categories;
  cacheStore.freeAgents.lastCategoriesFetch = Date.now();
  
  return categories;
};

/**
 * Get free agents by category (chunked loading)
 */
export const getFreeAgentsByCategory = async (categoryId, fetchFn, options = {}) => {
  const { forceRefresh = false, chunkSize = DEFAULT_CHUNK_SIZE } = options;
  const cacheKey = categoryId || 'all';
  
  if (!cacheStore.freeAgents.byCategory[cacheKey]) {
    cacheStore.freeAgents.byCategory[cacheKey] = {
      items: [],
      offset: 0,
      hasMore: true,
      lastFetch: null,
    };
  }
  
  const categoryCache = cacheStore.freeAgents.byCategory[cacheKey];
  
  if (!forceRefresh && categoryCache.items.length > 0 && isCacheValid(categoryCache.lastFetch)) {
    return {
      items: categoryCache.items,
      hasMore: categoryCache.hasMore,
      fromCache: true,
    };
  }
  
  if (forceRefresh) {
    categoryCache.items = [];
    categoryCache.offset = 0;
    categoryCache.hasMore = true;
  }
  
  const result = await fetchFn({
    categoryId,
    limit: chunkSize,
    offset: categoryCache.offset,
  });
  
  const newItems = result.users || result.agents || result.items || result || [];
  
  categoryCache.items = [...categoryCache.items, ...newItems];
  categoryCache.offset += newItems.length;
  categoryCache.hasMore = newItems.length >= chunkSize;
  categoryCache.lastFetch = Date.now();
  
  return {
    items: categoryCache.items,
    hasMore: categoryCache.hasMore,
    fromCache: false,
  };
};

/**
 * Load more free agents for a category
 */
export const loadMoreFreeAgents = async (categoryId, fetchFn, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const cacheKey = categoryId || 'all';
  const categoryCache = cacheStore.freeAgents.byCategory[cacheKey];
  
  if (!categoryCache || !categoryCache.hasMore) {
    return { items: categoryCache?.items || [], hasMore: false, loadedMore: false };
  }
  
  const result = await fetchFn({
    categoryId,
    limit: chunkSize,
    offset: categoryCache.offset,
  });
  
  const newItems = result.users || result.agents || result.items || result || [];
  
  categoryCache.items = [...categoryCache.items, ...newItems];
  categoryCache.offset += newItems.length;
  categoryCache.hasMore = newItems.length >= chunkSize;
  categoryCache.lastFetch = Date.now();
  
  return {
    items: categoryCache.items,
    hasMore: categoryCache.hasMore,
    loadedMore: newItems.length > 0,
    newItems,
  };
};

// =====================================================
// MY GROUPS CACHE
// =====================================================

/**
 * Get group types (with caching)
 */
export const getGroupTypes = async (fetchFn) => {
  if (cacheStore.myGroups.types && isCacheValid(cacheStore.myGroups.lastTypesFetch)) {
    return cacheStore.myGroups.types;
  }
  
  const types = await fetchFn();
  cacheStore.myGroups.types = types;
  cacheStore.myGroups.lastTypesFetch = Date.now();
  
  return types;
};

/**
 * Get my groups by type (chunked loading)
 */
export const getMyGroupsByType = async (typeId, fetchFn, options = {}) => {
  const { forceRefresh = false, chunkSize = DEFAULT_CHUNK_SIZE } = options;
  const cacheKey = typeId || 'all';
  
  if (!cacheStore.myGroups.byType[cacheKey]) {
    cacheStore.myGroups.byType[cacheKey] = {
      items: [],
      offset: 0,
      hasMore: true,
      lastFetch: null,
    };
  }
  
  const typeCache = cacheStore.myGroups.byType[cacheKey];
  
  if (!forceRefresh && typeCache.items.length > 0 && isCacheValid(typeCache.lastFetch)) {
    return {
      items: typeCache.items,
      hasMore: typeCache.hasMore,
      fromCache: true,
    };
  }
  
  if (forceRefresh) {
    typeCache.items = [];
    typeCache.offset = 0;
    typeCache.hasMore = true;
  }
  
  const result = await fetchFn({
    typeId,
    limit: chunkSize,
    offset: typeCache.offset,
  });
  
  const newItems = result.groups || result.communities || result.items || result || [];
  
  typeCache.items = [...typeCache.items, ...newItems];
  typeCache.offset += newItems.length;
  typeCache.hasMore = newItems.length >= chunkSize;
  typeCache.lastFetch = Date.now();
  
  return {
    items: typeCache.items,
    hasMore: typeCache.hasMore,
    fromCache: false,
  };
};

/**
 * Load more groups for a type
 */
export const loadMoreGroups = async (typeId, fetchFn, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const cacheKey = typeId || 'all';
  const typeCache = cacheStore.myGroups.byType[cacheKey];
  
  if (!typeCache || !typeCache.hasMore) {
    return { items: typeCache?.items || [], hasMore: false, loadedMore: false };
  }
  
  const result = await fetchFn({
    typeId,
    limit: chunkSize,
    offset: typeCache.offset,
  });
  
  const newItems = result.groups || result.communities || result.items || result || [];
  
  typeCache.items = [...typeCache.items, ...newItems];
  typeCache.offset += newItems.length;
  typeCache.hasMore = newItems.length >= chunkSize;
  typeCache.lastFetch = Date.now();
  
  return {
    items: typeCache.items,
    hasMore: typeCache.hasMore,
    loadedMore: newItems.length > 0,
    newItems,
  };
};

// =====================================================
// INDEXEDDB PERSISTENCE
// =====================================================

const DB_NAME = 'aiia_cache';
const DB_VERSION = 1;

let dbInstance = null;

const getDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }
    
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Store for cache data
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    };
  });
};

const saveToIndexedDB = async (userId) => {
  try {
    const db = await getDB();
    const tx = db.transaction('cache', 'readwrite');
    const store = tx.objectStore('cache');
    
    await store.put({
      key: `user_${userId}`,
      data: {
        communities: cacheStore.communities,
        freeAgents: cacheStore.freeAgents,
        myGroups: cacheStore.myGroups,
      },
      timestamp: Date.now(),
      version: CACHE_VERSION,
    });
  } catch (e) {
    console.warn('Failed to save cache to IndexedDB:', e);
  }
};

const restoreFromIndexedDB = async (userId) => {
  try {
    const db = await getDB();
    const tx = db.transaction('cache', 'readonly');
    const store = tx.objectStore('cache');
    
    const result = await new Promise((resolve, reject) => {
      const request = store.get(`user_${userId}`);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
    
    if (result && result.version === CACHE_VERSION) {
      // Check if data is not too old (max 1 hour)
      if (Date.now() - result.timestamp < 60 * 60 * 1000) {
        Object.assign(cacheStore.communities, result.data.communities);
        Object.assign(cacheStore.freeAgents, result.data.freeAgents);
        Object.assign(cacheStore.myGroups, result.data.myGroups);
      }
    }
  } catch (e) {
    console.warn('Failed to restore cache from IndexedDB:', e);
  }
};

// =====================================================
// CACHE UTILITIES
// =====================================================

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  const countItems = (obj) => {
    return Object.values(obj).reduce((sum, cat) => sum + (cat.items?.length || 0), 0);
  };
  
  return {
    communities: {
      categories: cacheStore.communities.categories?.length || 0,
      items: countItems(cacheStore.communities.byCategory),
    },
    freeAgents: {
      categories: cacheStore.freeAgents.categories?.length || 0,
      items: countItems(cacheStore.freeAgents.byCategory),
    },
    myGroups: {
      types: cacheStore.myGroups.types?.length || 0,
      items: countItems(cacheStore.myGroups.byType),
    },
  };
};

/**
 * Invalidate specific cache entries
 */
export const invalidateCache = (type, categoryId = null) => {
  switch (type) {
    case 'communities':
      if (categoryId) {
        delete cacheStore.communities.byCategory[categoryId];
      } else {
        cacheStore.communities.byCategory = {};
        cacheStore.communities.categories = null;
      }
      break;
    case 'freeAgents':
      if (categoryId) {
        delete cacheStore.freeAgents.byCategory[categoryId];
      } else {
        cacheStore.freeAgents.byCategory = {};
        cacheStore.freeAgents.categories = null;
      }
      break;
    case 'myGroups':
      if (categoryId) {
        delete cacheStore.myGroups.byType[categoryId];
      } else {
        cacheStore.myGroups.byType = {};
        cacheStore.myGroups.types = null;
      }
      break;
    default:
      clearCache();
  }
};

/**
 * Add item to cache (e.g., after creating a new community)
 */
export const addItemToCache = (type, categoryId, item) => {
  const cacheKey = categoryId || 'all';
  
  switch (type) {
    case 'communities':
      if (cacheStore.communities.byCategory[cacheKey]) {
        cacheStore.communities.byCategory[cacheKey].items.unshift(item);
      }
      break;
    case 'freeAgents':
      if (cacheStore.freeAgents.byCategory[cacheKey]) {
        cacheStore.freeAgents.byCategory[cacheKey].items.unshift(item);
      }
      break;
    case 'myGroups':
      if (cacheStore.myGroups.byType[cacheKey]) {
        cacheStore.myGroups.byType[cacheKey].items.unshift(item);
      }
      break;
  }
};

/**
 * Remove item from cache
 */
export const removeItemFromCache = (type, itemId) => {
  const removeFromCategory = (categoryCache) => {
    Object.values(categoryCache).forEach(cat => {
      if (cat.items) {
        cat.items = cat.items.filter(item => item.id !== itemId);
      }
    });
  };
  
  switch (type) {
    case 'communities':
      removeFromCategory(cacheStore.communities.byCategory);
      break;
    case 'freeAgents':
      removeFromCategory(cacheStore.freeAgents.byCategory);
      break;
    case 'myGroups':
      removeFromCategory(cacheStore.myGroups.byType);
      break;
  }
};

/**
 * Update item in cache
 */
export const updateItemInCache = (type, itemId, updates) => {
  const updateInCategory = (categoryCache) => {
    Object.values(categoryCache).forEach(cat => {
      if (cat.items) {
        const index = cat.items.findIndex(item => item.id === itemId);
        if (index !== -1) {
          cat.items[index] = { ...cat.items[index], ...updates };
        }
      }
    });
  };
  
  switch (type) {
    case 'communities':
      updateInCategory(cacheStore.communities.byCategory);
      break;
    case 'freeAgents':
      updateInCategory(cacheStore.freeAgents.byCategory);
      break;
    case 'myGroups':
      updateInCategory(cacheStore.myGroups.byType);
      break;
  }
};

// Auto-save to IndexedDB periodically
let saveTimeout = null;
const scheduleSave = () => {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    if (cacheMeta.userId) {
      saveToIndexedDB(cacheMeta.userId);
    }
  }, 5000);
};

// Export default for convenience
export default {
  initializeCache,
  clearCache,
  getCommunityCategories,
  getCommunitiesByCategory,
  loadMoreCommunities,
  getFreeAgentCategories,
  getFreeAgentsByCategory,
  loadMoreFreeAgents,
  getGroupTypes,
  getMyGroupsByType,
  loadMoreGroups,
  getCacheStats,
  invalidateCache,
  addItemToCache,
  removeItemFromCache,
  updateItemInCache,
};
